/**
 * A tiny external store for one token-bearing session, kept in `sessionStorage` (the approved storage
 * for temporary tokens: it lives only as long as the tab). Guest and staff each get their own instance
 * and their own key, so neither can read, extend or clear the other.
 *
 * - A stored value that cannot be read back is dropped (`invalid`).
 * - A session past `expiresAt` is dropped the moment it is read, and by a timer for open pages (`expired`).
 * - If storage is unavailable (private mode, quota) the session stays in memory for the page's life.
 */

export type SessionEndReason = 'expired' | 'invalid' | 'unauthorized' | 'signed-out';

export interface SessionStore<T extends { expiresAt: number | null }> {
  /** Current session, or null. Safe to call during render; the reference is stable until a change. */
  get(): T | null;
  set(session: T): void;
  clear(reason?: SessionEndReason): void;
  subscribe(listener: () => void): () => void;
  /** Why the last session ended during this page load; null if none ended or a new one began. */
  endReason(): SessionEndReason | null;
}

export interface SessionStoreOptions<T> {
  key: string;
  /** Returns the typed session for a parsed JSON value, or null if it is not one. */
  parse: (raw: unknown) => T | null;
  now?: () => number;
  storage?: () => Storage | null;
}

const MAX_TIMER_MS = 2_147_483_647;

export function createSessionStore<T extends { expiresAt: number | null }>(
  options: SessionStoreOptions<T>,
): SessionStore<T> {
  const now = options.now ?? (() => Date.now());
  const listeners = new Set<() => void>();
  let loaded = false;
  let current: T | null = null;
  let reason: SessionEndReason | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function storage(): Storage | null {
    try {
      return options.storage ? options.storage() : window.sessionStorage;
    } catch {
      return null;
    }
  }

  function removeStored(): void {
    try {
      storage()?.removeItem(options.key);
    } catch {
      /* storage unavailable: nothing to remove */
    }
  }

  function notify(): void {
    listeners.forEach((listener) => listener());
  }

  function isExpired(session: T): boolean {
    return session.expiresAt !== null && now() >= session.expiresAt;
  }

  function cancelTimer(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function schedule(): void {
    cancelTimer();
    if (!current || current.expiresAt === null) return;
    const delay = Math.min(Math.max(current.expiresAt - now(), 0), MAX_TIMER_MS);
    timer = setTimeout(() => {
      if (current && isExpired(current)) end('expired');
      else schedule();
    }, delay);
  }

  function end(why: SessionEndReason): void {
    current = null;
    reason = why;
    cancelTimer();
    removeStored();
    notify();
  }

  /** Reads storage once. Never notifies: it runs inside `get()`, which React may call during render. */
  function load(): void {
    loaded = true;
    let text: string | null = null;
    try {
      text = storage()?.getItem(options.key) ?? null;
    } catch {
      text = null;
    }
    if (text === null) return;

    let parsed: T | null = null;
    try {
      parsed = options.parse(JSON.parse(text));
    } catch {
      parsed = null;
    }
    if (!parsed) {
      removeStored();
      reason = 'invalid';
    } else if (isExpired(parsed)) {
      removeStored();
      reason = 'expired';
    } else {
      current = parsed;
      schedule();
    }
  }

  return {
    get() {
      if (!loaded) {
        load();
      } else if (current && isExpired(current)) {
        // Backstop for a timer that did not fire (sleeping device, throttled tab). Notify after render.
        current = null;
        reason = 'expired';
        cancelTimer();
        removeStored();
        queueMicrotask(notify);
      }
      return current;
    },

    set(session) {
      loaded = true;
      if (isExpired(session)) {
        end('expired');
        return;
      }
      current = session;
      reason = null;
      try {
        storage()?.setItem(options.key, JSON.stringify(session));
      } catch {
        /* storage unavailable: keep it in memory only */
      }
      schedule();
      notify();
    },

    clear(why = 'signed-out') {
      loaded = true;
      end(why);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    endReason: () => reason,
  };
}
